// Content for the in-admin Help / Guide page (AdminHelp.jsx) and its printable
// version. Each section corresponds 1:1 to an item in AdminLayout's NAV list.
//
// screenshots: [{ src, alt, label?, annotations }] — an array of real
//   screenshots for this section (or subsection). `label` is shown as a small
//   heading above the image whenever a section has more than one screenshot
//   (e.g. "List View" vs "Add / Edit Form"). An empty array renders a
//   "Screenshot pending" placeholder.
// annotations (per screenshot): [{ x, y, text, from?: { x, y } }] — x/y are
//   percentages (0-100) of that screenshot's width/height, based on where the
//   described button/field actually sits in the image.
// fields: the Add/Edit form fields, in the exact order and with the exact
// label text shown in the UI, plus any hint already shown on-screen.
// notes: behavior a first-time admin would not guess on their own.

import loginImg from '../assests/manual/login.png';

import dashboardImg from '../assests/manual/dashboard.png';

import heroSlidesListImg from '../assests/manual/hero-slides.png';
import heroSlideFormImg from '../assests/manual/add-edit/hero-slides-edit.png';

import categoriesListImg from '../assests/manual/categories.png';
import categoryFormImg from '../assests/manual/add-edit/categories-edit.png';

import productsListImg from '../assests/manual/products.png';
import productFormImg1 from '../assests/manual/add-edit/products-edit-1.png';
import productFormImg2 from '../assests/manual/add-edit/products-edit-2.png';

import brandsListImg from '../assests/manual/brand.png';
import brandFormImg from '../assests/manual/add-edit/brands-edit.png';
import brandProductLineDropdownImg from '../assests/manual/add-edit/brands-add-dropdown-categ.png';

import clientsListImg from '../assests/manual/clients.png';
import clientFormImg from '../assests/manual/add-edit/clients-edit.png';

import careersIntroImg from '../assests/manual/careers.png';
import careersJobOpeningsImg from '../assests/manual/job-openings.png';
import jobOpeningFormImg from '../assests/manual/add-edit/job-opening-edit.png';
import careersApplicationsImg from '../assests/manual/add-edit/applicantions.png';

import promotionalListImg from '../assests/manual/promotional.png';
import promotionalFormImg1 from '../assests/manual/add-edit/promotional-edit-1.png';
import promotionalFormImg2 from '../assests/manual/add-edit/promotional-edit-2.png';

import messagesEmptyImg from '../assests/manual/messages.png';
import messageDetailImg from '../assests/manual/add-edit/message-in.png';
import messageReplyImg from '../assests/manual/add-edit/message-reply.png';

import settingsGeneral1Img from '../assests/manual/site-settings-general-1.png';
import settingsGeneral2Img from '../assests/manual/site-settings-general-2.png';
import settingsGeneral3Img from '../assests/manual/site-settings-general-3.png';
import settingsPasswordImg from '../assests/manual/site-setting-password.png';
import settingsUsersListImg from '../assests/manual/site-settings-users.png';
import adminUserAddFormImg from '../assests/manual/add-edit/users-add.png';
import adminUserEditFormImg from '../assests/manual/add-edit/users-edit.png';

export const HELP_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    overview:
      'How to log in, and the difference between the two account types you\'ll see across the panel.',
    screenshots: [
      {
        src: loginImg,
        alt: 'Admin login screen with Email and Password fields',
        annotations: [
          { x: 49, y: 53, text: 'Email — this is your login name, e.g. name@sankyomt.ph.' },
          { x: 49, y: 61, text: 'Password.' },
          { x: 49, y: 68, text: 'Sign In — on success you land on the Dashboard.' },
        ],
      },
    ],
    fields: [
      { label: 'Email', hint: 'e.g. name@sankyomt.ph — doesn\'t need to be a real inbox, it\'s just your login name.' },
      { label: 'Password', hint: '' },
    ],
    notes: [
      'Log in at admin.sankyomt.ph (or yoursite.com/admin/login, both work) with the email and password an Admin created for you.',
      'If your email/password don\'t match, you\'ll see "Invalid email or password." — it won\'t tell you which one was wrong, for security. Double check both before asking an Admin to reset it.',
      'There are two account roles: Admin and Editor. Admins can do everything. Editors can manage day-to-day content — Hero Slides, Categories, Products, Brands, Clients, Careers, Promotional Posts, and Messages — but cannot open Site Settings or manage other accounts.',
      'If a menu item described in this guide (like "Site Settings") doesn\'t appear in your sidebar, it\'s because your account is an Editor, not an Admin — this is expected, not an error.',
      'There\'s no "Forgot Password" link — if you\'re locked out, ask an Admin to reset your password from Site Settings > Admin Users.',
    ],
  },
  {
    id: 'where-it-appears',
    title: 'Where Things Appear',
    overview: 'A quick map from each admin section to the actual page on the live site it controls — for orientation before you start editing.',
    screenshots: [],
    fieldsHeading: 'Admin Section → Live Page',
    fields: [
      { label: 'Hero Slides', hint: 'The rotating banner slideshow at the very top of the homepage (yoursite.com/).' },
      { label: 'Categories', hint: 'Category browsing on the Products page (yoursite.com/products) and its sub-pages.' },
      { label: 'Products', hint: 'Individual product cards and detail pages, reached by browsing a Category, then a Brand.' },
      { label: 'Brands', hint: 'The Brands page (yoursite.com/brands), each brand\'s Categories → Sub Categories pages, plus the brand step when browsing Products by category.' },
      { label: 'Clients', hint: 'The Clients page (yoursite.com/clients) and each client\'s own detail page when clicked.' },
      { label: 'Careers', hint: 'The public Careers page (yoursite.com/careers) — intro text, job listings, and where the application form submits to.' },
      { label: 'Promotional Posts', hint: 'The Promotional page (yoursite.com/promotional) — its Highlight/Announcement/Event tabs.' },
      { label: 'Messages', hint: 'Nothing is published here — this inbox receives whatever visitors submit through the public Contact page (yoursite.com/contact).' },
      { label: 'Site Settings — General', hint: 'Site-wide: the footer (phone/email/social links), the Company Info page\'s About, Mission, and Company Details sections, the homepage stats box, and the Contact page\'s details.' },
      { label: 'Site Settings — Change Password / Admin Users', hint: 'Nothing on the live site — these only control who can log into this admin panel.' },
    ],
    notes: [
      'This is just a map, not a duplicate of the site — each admin section above still has its own full page later in this guide with the real form fields and screenshots.',
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    overview:
      'The home screen after logging in — a quick overview of the site\'s content and traffic, with shortcuts to common tasks.',
    screenshots: [
      {
        src: dashboardImg,
        alt: 'Admin dashboard with stat tiles, most viewed content, and quick actions',
        annotations: [
          { x: 18, y: 20, text: 'Every stat tile is clickable — it jumps straight to that section.' },
          { x: 35, y: 48, text: 'Unique visitors, with total page loads in parentheses.' },
          { x: 78, y: 48, text: 'Same breakdown for your top-performing products.' },
          { x: 19, y: 66, text: 'Quick Actions jump straight to the Add-item screen for the busiest sections.' },
        ],
      },
    ],
    fields: [],
    notes: [
      'The counter tiles (Products, Categories, Clients, Hero Slides, Messages, Page Views, Unique Visitors) are clickable — they jump straight to that section.',
      'If you have unread messages, a yellow banner appears at the top with a link straight to the Messages page.',
      '"Most Viewed Categories" and "Most Viewed Products" show which pages visitors look at most — the bold number is unique visitors, the number in parentheses is total page loads (a returning visitor who checks a page twice counts once as "unique" but twice as a raw view).',
      'The "Quick Actions" buttons at the bottom jump directly to the Add-item view of the most commonly used sections.',
    ],
  },
  {
    id: 'hero',
    title: 'Hero Slides',
    overview: 'Manages the rotating image slideshow at the very top of the homepage.',
    screenshots: [
      {
        src: heroSlidesListImg,
        alt: 'Hero Slides list',
        label: 'List View',
        annotations: [
          { x: 95, y: 10, text: 'Add Slide — new slides are automatically placed at the end of the order.' },
          { x: 31, y: 21, text: 'A slide with no Title shows as image-only, with no text overlay — like this one.' },
          { x: 96, y: 21, text: 'Edit or delete this slide.' },
        ],
      },
      {
        src: heroSlideFormImg,
        alt: 'Edit Hero Slide form',
        label: 'Add / Edit Form',
        annotations: [
          { x: 49, y: 53, text: 'Existing image preview — upload a new file, or paste a URL below to replace it.' },
          { x: 38, y: 68, text: 'Dual image input: choose a file, or paste an image URL instead — either works.' },
          { x: 49, y: 82, text: 'Sort Order — change this to reposition the slide; other slides shift automatically to make room.' },
          { x: 43, y: 90, text: 'Save Slide.' },
        ],
      },
    ],
    fields: [
      { label: 'Title', hint: 'Leave blank for an image-only slide with no text overlay — e.g. a graphic that already has its own text baked in.' },
      { label: 'Title (Japanese)', hint: 'Falls back to Title if left blank.' },
      { label: 'Subtitle', hint: '' },
      { label: 'Subtitle (Japanese)', hint: 'Falls back to Subtitle if left blank.' },
      { label: 'Image', hint: 'Upload a file, or paste an image URL instead. An image is required to save a slide.' },
      { label: 'Sort Order', hint: 'Controls left-to-right / slide order — see note below.' },
      { label: 'Status', hint: 'Active or Hidden.' },
    ],
    notes: [
      'New slides are automatically placed at the end of the order — you don\'t need to figure out the next number yourself.',
      'If you change a slide\'s Sort Order to match another slide\'s position, the other slides shift automatically to make room — you never end up with two slides sharing the same number.',
      'A "Hidden" slide stays saved in the list but will not appear in the live homepage slideshow, so you can prepare a slide in advance and switch it to Active when ready.',
      'The homepage slideshow uses the same wide banner proportions as the reference site (sankyo-seiki.com) — for the cleanest look, upload images sized around 1920×800px.',
    ],
  },
  {
    id: 'categories',
    title: 'Categories',
    overview: 'Manages the product categories used to organize Products throughout the site.',
    screenshots: [
      {
        src: categoriesListImg,
        alt: 'Product Categories list',
        label: 'List View',
        annotations: [
          { x: 96, y: 10, text: 'Add Category — new categories go to the end of the order.' },
          { x: 87, y: 22, text: 'Views column: unique visitors, with total page loads in parentheses.' },
          { x: 94, y: 22, text: 'Edit or delete this category.' },
        ],
      },
      {
        src: categoryFormImg,
        alt: 'Edit Category form',
        label: 'Add / Edit Form',
        annotations: [
          { x: 49, y: 30, text: 'Slug — auto-fills from Name only while adding; editing an existing category won\'t touch it automatically.' },
          { x: 49, y: 57, text: 'Existing banner image preview — replace it with a new file or URL below.' },
          { x: 49, y: 69, text: 'Choose a file, or paste an image URL instead.' },
          { x: 43, y: 84, text: 'Save Category.' },
        ],
      },
    ],
    fields: [
      { label: 'Name *', hint: '' },
      { label: 'Slug *', hint: 'Used in the URL. Auto-generated from name.' },
      { label: 'Description', hint: '' },
      { label: 'Banner Image', hint: 'Upload a file, or paste an image URL instead.' },
      { label: 'Sort Order', hint: '' },
      { label: 'Status', hint: 'Active or Hidden.' },
    ],
    notes: [
      'The Slug auto-fills from the Name only while adding a new category. When editing an existing one, changing the Name no longer changes the Slug automatically — edit it by hand if needed.',
      'New categories are automatically placed at the end of the sort order; reordering shifts other categories to make room, same as Hero Slides.',
      'Deleting a category asks you to confirm: "Delete this category? Products in it will lose their category." — those products aren\'t deleted, they just become uncategorized.',
      'The table shows a Views column: unique visitors, with total page loads in parentheses.',
    ],
  },
  {
    id: 'products',
    title: 'Products',
    overview: 'Manages the product catalog — names, images, specifications, and category/brand assignment.',
    screenshots: [
      {
        src: productsListImg,
        alt: 'Products list',
        label: 'List View',
        annotations: [
          { x: 96, y: 10, text: 'Add Product.' },
          { x: 30, y: 20, text: 'Search box matches product name and brand name; use the category filter alongside it.' },
          { x: 94, y: 33, text: 'Edit or delete this product.' },
        ],
      },
      {
        src: productFormImg1,
        alt: 'Edit Product form — name, category, brand, and specifications',
        label: 'Add / Edit Form — Fields',
        annotations: [
          { x: 49, y: 17, text: 'Product Name.' },
          { x: 58, y: 25, text: 'Category — independent from Brand; a product can have one, both, or neither.' },
          { x: 41, y: 34, text: 'Brand — controls which brand page this product appears on. Manage brands separately under the Brands page.' },
          { x: 49, y: 61, text: 'Specifications supports ## headings and - bullet lists; blank lines start a new paragraph.' },
        ],
      },
      {
        src: productFormImg2,
        alt: 'Edit Product form — cover image and additional gallery images',
        label: 'Add / Edit Form — Images',
        annotations: [
          { x: 49, y: 60, text: 'Cover Image — the single main photo, used on cards and as the first gallery image.' },
          { x: 36, y: 69, text: 'Additional Gallery Images — select multiple files at once to add several more photos.' },
          { x: 41, y: 87, text: 'Save Product.' },
        ],
      },
    ],
    fields: [
      { label: 'Product Name *', hint: '' },
      { label: 'Slug *', hint: '' },
      { label: 'Category', hint: '"— No Category —" or pick one.' },
      { label: 'Brand', hint: 'Controls where this product appears when browsing by brand. Manage brands under the Brands page.' },
      { label: 'Status', hint: 'Active or Hidden.' },
      { label: 'Description', hint: '' },
      { label: 'Specifications', hint: 'Start a line with ## for a heading, - for a bullet list. Blank lines start a new paragraph. Links are made clickable automatically.' },
      { label: 'Cover Image', hint: 'Shown on product cards and as the main gallery image.' },
      { label: 'Additional Gallery Images', hint: 'Extra photos shown in the gallery on the product page. Select multiple files at once.' },
    ],
    notes: [
      'Cover Image is the single main photo (used on cards and as the first gallery image); Additional Gallery Images lets you add several more photos at once, each individually removable from a thumbnail grid.',
      'Category and Brand are independent — a product can have one, both, or neither. Leaving Brand unassigned means the product won\'t show up on that brand\'s page.',
      'Slug auto-fills from Product Name only when adding a new product, same rule as Categories.',
      'The product list has a search box (matches product name and brand name) and a category filter, useful once your catalog grows.',
      'New products are automatically placed at the end of the sort order.',
    ],
  },
  {
    id: 'brands',
    title: 'Brands',
    overview: 'Manages the brand list Sankyo carries, and each brand\'s own Categories and Sub Categories shown when a visitor clicks the brand on the public Brands page.',
    screenshots: [
      {
        src: brandsListImg,
        alt: 'Brands grid',
        label: 'List View',
        annotations: [
          { x: 96, y: 9, text: 'Add Brand.' },
          { x: 83, y: 29, text: '"No product line assigned" is fine — the brand\'s card still links to its own product listing.' },
          { x: 33, y: 35, text: 'Edit or delete this brand.' },
        ],
      },
      {
        src: brandFormImg,
        alt: 'Edit Brand form',
        label: 'Add / Edit Form',
        annotations: [
          { x: 49, y: 37, text: 'Product Line — same list as Categories, just labeled differently here; it groups brands the same way categories group products.' },
          { x: 49, y: 61, text: 'Existing logo preview — replace it with a new file or URL below.' },
          { x: 41, y: 86, text: 'Save Brand.' },
        ],
      },
      {
        src: brandProductLineDropdownImg,
        alt: 'Product Line dropdown open, showing the same list as Categories',
        label: 'Product Line Dropdown',
        annotations: [
          { x: 49, y: 38, text: 'Product Line reuses the exact same list as Categories — pick the section this brand should appear under on the public Brands page.' },
          { x: 49, y: 45, text: 'The currently selected option is highlighted in blue.' },
          { x: 49, y: 59, text: 'Options here mirror your Categories list exactly — add a category first if the one you need isn\'t here.' },
        ],
      },
    ],
    fields: [
      { label: 'Name *', hint: '' },
      { label: 'Slug *', hint: '' },
      { label: 'Product Line', hint: 'Which section this brand appears under on the Brands page.' },
      { label: 'Description', hint: '' },
      { label: 'Logo', hint: 'Upload a file, or paste an image URL instead.' },
      { label: 'Sort Order', hint: '' },
      { label: 'Status', hint: 'Active or Hidden.' },
    ],
    notes: [
      '"Product Line" is the same list as Categories, just labeled differently here — it groups brands the same way categories group products.',
      'The public Brands page lists names by Product Line, alphabetically, or via a name search. A brand with no Product Line is listed under "Other".',
      'Clicking a brand opens its Categories (e.g. Machine Cutting Tools); clicking a category shows its Sub Categories (e.g. Disc Cutting); clicking a sub category shows a Contact Us button. The Contact form then opens with the message already naming what the visitor was looking at.',
      'Manage them with the "Categories (n)" button on each brand card: "Add Category" for a top-level category, "+ Sub Category" on a category row for one beneath it. Each can have a description, an image, a sort order, and be set Hidden.',
      'A category with no sub categories (or a brand with no categories) goes straight to the Contact Us button. Deleting a category also deletes its sub categories.',
      'Slug auto-fills from Name only when adding a new brand.',
      'Deleting a brand asks you to confirm, and also deletes its categories and sub categories. Products assigned to it aren\'t deleted — they just become unassigned.',
    ],
  },
  {
    id: 'clients',
    title: 'Clients',
    overview: 'Manages the list of client/partner logos shown on the site (a separate roster from Brands — not linked to products).',
    screenshots: [
      {
        src: clientsListImg,
        alt: 'Clients grid',
        label: 'List View',
        annotations: [
          { x: 96, y: 10, text: 'Add Client.' },
          { x: 26, y: 29, text: 'Clients don\'t need a Product Line — this list isn\'t linked to Categories or Products at all.' },
          { x: 33, y: 32, text: 'Edit or delete this client.' },
        ],
      },
      {
        src: clientFormImg,
        alt: 'Edit Client form',
        label: 'Add / Edit Form',
        annotations: [
          { x: 49, y: 34, text: 'Slug — auto-fills from Name only while adding a new client.' },
          { x: 49, y: 56, text: 'Existing logo preview — replace it with a new file or URL below.' },
          { x: 41, y: 81, text: 'Save Client.' },
        ],
      },
    ],
    fields: [
      { label: 'Name *', hint: '' },
      { label: 'Slug *', hint: '' },
      { label: 'Description', hint: '' },
      { label: 'Logo', hint: 'Upload a file, or paste an image URL instead.' },
      { label: 'Sort Order', hint: '' },
      { label: 'Status', hint: 'Active or Hidden.' },
    ],
    notes: [
      'Clients are a different concept from Brands: Brands are the manufacturers behind the products you sell; Clients are companies you\'ve worked with/for — the two lists are unrelated.',
      'Slug auto-fills from Name only when adding a new client.',
    ],
  },
  {
    id: 'careers',
    title: 'Careers',
    overview: 'One page that manages three things: the Careers page intro text, the job listings, and incoming applications.',
    screenshots: [
      {
        src: careersIntroImg,
        alt: 'Careers page intro section form',
        label: 'Intro Section',
        annotations: [
          { x: 49, y: 15, text: 'Intro Content — separate paragraphs with a blank line, same as other rich text fields in this panel.' },
          { x: 31, y: 78, text: 'Dual image input — choose a file, or paste an image URL instead.' },
          { x: 31, y: 85, text: 'Save Intro Section — this only saves the intro fields above, separate from the job listings further down the page.' },
        ],
      },
      {
        src: careersJobOpeningsImg,
        alt: 'Job Openings and Applications tables, empty state',
        label: 'Job Openings & Applications',
        annotations: [
          { x: 94, y: 53, text: 'Add Job Opening.' },
          { x: 57, y: 64, text: 'Job Openings table — empty until you add your first listing.' },
          { x: 57, y: 85, text: 'Applications table — fills automatically as candidates apply through the public Careers page.' },
        ],
      },
      {
        src: jobOpeningFormImg,
        alt: 'Edit Job Opening form',
        label: 'Job Opening Form',
        annotations: [
          { x: 58, y: 28, text: 'Code Number — your own reference code shown to applicants, e.g. "ACCA-SRB2K24".' },
          { x: 49, y: 66, text: 'Qualifications — one per line; shown as a bullet list on the public page.' },
          { x: 41, y: 86, text: 'Save Job Opening.' },
        ],
      },
      {
        src: careersApplicationsImg,
        alt: 'Job Openings and Applications tables with a real application entry',
        label: 'Applications — Real Entry',
        annotations: [
          { x: 22, y: 84, text: '"1 unread" badge, and an envelope icon on the row — clicking the row marks it read.' },
          { x: 52, y: 92, text: 'Contact info is shown directly in the table — email and phone, no need to open anything else.' },
          { x: 86, y: 92, text: 'View opens the applicant\'s uploaded resume.' },
        ],
      },
    ],
    fields: [
      { label: 'Intro Title', hint: '' },
      { label: 'Intro Content', hint: 'Separate paragraphs with a blank line.' },
      { label: 'Intro Title (Japanese)', hint: 'Falls back to the English title if left blank.' },
      { label: 'Intro Content (Japanese)', hint: 'Falls back to the English content if left blank.' },
      { label: 'Image', hint: 'Upload a file, or paste an image URL instead.' },
    ],
    notes: [
      'The Intro Section (fields above) has its own "Save Intro Section" button, separate from the job listings below it.',
      'Each Job Opening has its own Add/Edit form: Title, Slug, Code Number (e.g. "ACCA-SRB2K24" — your own reference code shown to applicants), Location, Employment Type (Full-time/Part-time/Contract/Internship), Job Overview, Qualifications (one per line — shown as a bullet list), Sort Order, and Status.',
      'Applications are viewed in a table further down the page — unread ones show an envelope icon; clicking a row marks it read. Each row shows the applicant\'s name, which job they applied for, their contact info, when they applied, and a "View" link to their uploaded resume.',
      'There\'s no reply feature inside the panel — use the applicant\'s listed email directly. You can only view the resume and delete the application once handled.',
      'When someone applies through the public Careers page, they automatically receive a confirmation email letting them know their application was received — this is sent by the system itself, there\'s nothing to configure or send manually from the admin panel.',
    ],
  },
  {
    id: 'promotions',
    title: 'Promotional Posts',
    overview: 'Manages the News / Announcements / Events feed shown on the public Promotional page.',
    screenshots: [
      {
        src: promotionalListImg,
        alt: 'Promotional posts list',
        label: 'List View',
        annotations: [
          { x: 96, y: 9, text: 'Add Post.' },
          { x: 58, y: 24, text: 'Category (Highlight, Announcement, or Event) controls where it shows on the public page.' },
          { x: 91, y: 24, text: 'Edit or delete this post.' },
        ],
      },
      {
        src: promotionalFormImg1,
        alt: 'Edit Post form — title, category, publish date, and rich-text toolbar',
        label: 'Add / Edit Form — Fields & Toolbar',
        annotations: [
          { x: 33, y: 31, text: 'Category — Highlight, Announcement, or Event; controls which tab it appears under on the public page.' },
          { x: 47, y: 50, text: 'Bold, Italic, Underline, font, and color controls.' },
          { x: 60, y: 50, text: 'Text alignment buttons.' },
          { x: 34, y: 54, text: 'Numbered and bullet list buttons.' },
          { x: 37, y: 54, text: 'Insert an image anywhere in the text.' },
          { x: 39, y: 54, text: 'Insert a video (YouTube, Facebook, or Instagram) — paste the URL and it embeds automatically.' },
        ],
      },
      {
        src: promotionalFormImg2,
        alt: 'Edit Post form — embedded video, cover image, sort order and status',
        label: 'Add / Edit Form — Image & Video',
        annotations: [
          { x: 50, y: 37, text: 'A video embedded directly in the content — inserted via the toolbar\'s video button by pasting the URL; costs zero storage since only the link is saved.' },
          { x: 50, y: 59, text: 'Cover Image — separate from any video in the content; shown on the list/featured card and at the top of the post\'s page.' },
          { x: 34, y: 87, text: 'Sort Order controls which post shows as the large featured card at the top of the public page (lowest number = featured), not the publish date.' },
          { x: 41, y: 94, text: 'Save Post.' },
        ],
      },
    ],
    fields: [
      { label: 'Title *', hint: '' },
      { label: 'Slug *', hint: 'The web address for this post — auto-fills from the Title, but can be edited by hand.' },
      { label: 'Category', hint: 'Highlight, Announcement, or Event — controls which tab it shows under on the public page.' },
      { label: 'Publish Date', hint: '' },
      { label: 'Excerpt', hint: 'Short one-line summary shown on the list.' },
      { label: 'Content', hint: 'Full rich-text editor — see note below.' },
      { label: 'Cover Image', hint: 'Shown on the list/featured card and at the top of the post\'s page. To add a video, use the video button inside the Content editor above instead.' },
      { label: 'Sort Order', hint: '' },
      { label: 'Status', hint: 'Active or Hidden.' },
    ],
    notes: [
      'The Content field is a full rich-text editor: Bold, Italic, Underline, font/color, text alignment, bullet/numbered lists, inserting images anywhere in the text, and embedding a video (YouTube, Facebook, or Instagram) anywhere in the text via its toolbar\'s video button — just paste the video\'s URL.',
      'Video is no longer a separate field — embed it inside the Content editor wherever you want it to appear in the post. This also means videos cost zero storage: only the URL is saved, the video itself stays hosted on YouTube/Facebook/Instagram.',
      'Sort Order controls which post appears as the large featured card at the top of the public page (lowest number = featured), not the publish date.',
      'New posts are automatically placed at the end of the order; reordering shifts other posts automatically, the same as Hero Slides.',
    ],
  },
  {
    id: 'messages',
    title: 'Messages',
    overview: 'An inbox for contact-form submissions from website visitors. Nothing to create here — just view and manage what comes in.',
    screenshots: [
      {
        src: messagesEmptyImg,
        alt: 'Messages inbox, empty state',
        label: 'Empty Inbox',
        annotations: [
          { x: 31, y: 12, text: 'Two-pane layout: message list on the left, full detail on the right — like an email inbox.' },
          { x: 31, y: 45, text: 'Message list — empty until a visitor submits the contact form.' },
          { x: 73, y: 45, text: 'Detail pane — shows the full message once you select one from the list on the left.' },
        ],
      },
      {
        src: messageDetailImg,
        alt: 'A real message selected, showing the detail pane',
        label: 'Message Detail',
        annotations: [
          { x: 16, y: 19, text: 'Unread messages show a filled envelope icon and bold sender name — clicking marks it read automatically.' },
          { x: 94, y: 19, text: 'Reply opens your computer\'s default mail app addressed to the sender.' },
          { x: 52, y: 23, text: 'Email is clickable too — opens your mail app the same way.' },
          { x: 52, y: 44, text: 'Any attached images appear here as clickable thumbnails.' },
        ],
      },
      {
        src: messageReplyImg,
        alt: 'Browser prompt to open the default mail app when replying',
        label: 'Reply Behavior',
        annotations: [
          { x: 47, y: 9, text: 'Clicking "Reply" (or the sender\'s email) asks to open your computer\'s default mail app — Sankyo doesn\'t send anything automatically from inside the panel.' },
          { x: 55, y: 16, text: 'Confirm here to actually open your mail app, pre-addressed to the sender.' },
          { x: 61, y: 16, text: 'Cancel to stay in the panel without opening anything.' },
        ],
      },
    ],
    fields: [],
    notes: [
      'Two-pane layout: the message list on the left, the selected message\'s full detail on the right — like an email inbox.',
      'Unread messages show a filled envelope icon and bold sender name; clicking a message marks it read automatically.',
      'The detail pane shows the sender\'s Name, Email (click to open your mail app), Phone and Company if provided, when it was received, the full message text, and any attached images as clickable thumbnails.',
      '"Reply" opens your computer\'s default mail app addressed to the sender — Sankyo doesn\'t send anything automatically.',
      'Deleting a message asks you to confirm first.',
    ],
  },
  {
    id: 'settings',
    title: 'Site Settings',
    overview:
      'Admin-only. Site-wide contact info, the About and Mission section text, homepage stats, social links, your password, and other admin accounts.',
    screenshots: [
      {
        src: settingsGeneral1Img,
        alt: 'Site Settings General tab — site name, phone, email, and social links',
        label: 'General — Contact Info',
        annotations: [
          { x: 34, y: 18, text: 'Tabs: General, Change Password, and Admin Users — the latter two are covered further down this page.' },
          { x: 32, y: 55, text: 'Additional Phone Numbers — click "Add another" for more than one; shown alongside the primary number.' },
          { x: 32, y: 76, text: 'Additional Email Addresses work the same way.' },
        ],
      },
      {
        src: settingsGeneral2Img,
        alt: 'Site Settings General tab — office address and About section',
        label: 'General — About Section',
        annotations: [
          { x: 32, y: 8, text: 'Facebook/Instagram Page URLs — shown as icon links in the footer and contact page.' },
          { x: 32, y: 39, text: 'About Section — this is the text block on the public homepage\'s About area.' },
          { x: 32, y: 78, text: 'Japanese fields fall back to the English text automatically if left blank — no need to translate everything at once.' },
        ],
      },
      {
        src: settingsGeneral3Img,
        alt: 'Site Settings General tab — Mission section and Save Settings button',
        label: 'General — Mission Section',
        annotations: [
          { x: 32, y: 9, text: 'About Section Content (Japanese) continues here from the previous field above.' },
          { x: 32, y: 37, text: 'Mission Section — same pattern as About: English + optional Japanese, falling back automatically.' },
          { x: 31, y: 89, text: 'Save Settings — saves everything on this General tab in one action, not per-field.' },
        ],
      },
    ],
    fields: [
      { label: 'Site Name', hint: '' },
      { label: 'Phone Number', hint: '' },
      { label: 'Contact Email', hint: '' },
      { label: 'Additional Phone Numbers', hint: 'Shown alongside the primary phone number in the footer and contact page. Click "Add another" for more than one.' },
      { label: 'Additional Email Addresses', hint: 'Shown alongside the primary email in the footer and contact page.' },
      { label: 'Facebook Page URL', hint: 'e.g. https://facebook.com/sankyomt' },
      { label: 'Instagram Page URL', hint: 'e.g. https://instagram.com/sankyomt' },
      { label: 'Office Address', hint: '' },
      { label: 'About Section Title', hint: '' },
      { label: 'About Section Title (Japanese)', hint: 'Falls back to the English title if left blank.' },
      { label: 'About Section Content', hint: 'Separate paragraphs with a blank line.' },
      { label: 'About Section Content (Japanese)', hint: 'Separate paragraphs with a blank line. Falls back to the English content if left blank.' },
      { label: 'Mission Section Title', hint: '' },
      { label: 'Mission Section Title (Japanese)', hint: 'Falls back to the English title if left blank.' },
      { label: 'Mission Section Content', hint: '' },
      { label: 'Mission Section Content (Japanese)', hint: 'Falls back to the English content if left blank.' },
      { label: 'Company Details: Established', hint: 'e.g. 2016 (Philippines) — shown in the sidebar card on the Company Info page.' },
      { label: 'Company Details: Established (Japanese)', hint: 'Falls back to the English value if left blank.' },
      { label: 'Company Details: Parent Company', hint: 'e.g. Sankyo Seiki Co., Ltd.' },
      { label: 'Company Details: Parent Company (Japanese)', hint: 'Falls back to the English value if left blank.' },
      { label: 'Company Details: Headquarters', hint: 'e.g. Kyoto, Japan' },
      { label: 'Company Details: Headquarters (Japanese)', hint: 'Falls back to the English value if left blank.' },
      { label: 'Company Details: Specialization', hint: 'e.g. Precision Machinery & Measuring Equipment' },
      { label: 'Company Details: Specialization (Japanese)', hint: 'Falls back to the English value if left blank.' },
      { label: 'Company Details: Region', hint: 'e.g. Philippines & South-East Asia' },
      { label: 'Company Details: Region (Japanese)', hint: 'Falls back to the English value if left blank.' },
      { label: 'Stat: Years of Excellence', hint: 'e.g. 70+ — shown on the homepage stats box.' },
      { label: 'Stat: Established Year', hint: 'e.g. 2016' },
      { label: 'Stat: Products Available', hint: 'e.g. 100+' },
      { label: 'Stat: Major Clients', hint: 'e.g. 5+' },
    ],
    notes: [
      'This whole tab (and the "Admin Users" tab next to it) only appears for Admin accounts — Editors will only see "Change Password".',
      'The four "Stat" fields control the numbers shown in the homepage stats box (Years of Excellence, Established, Products Available, Major Clients) — if left blank they fall back to sensible defaults.',
      'Any field left blank for the Japanese version automatically falls back to showing the English text instead, so translating everything at once isn\'t required.',
      'Remember to click "Save Settings" at the bottom — it saves everything on the page in one action, not per-field.',
    ],
    subsections: [
      {
        title: 'Change Password (every account)',
        screenshots: [
          {
            src: settingsPasswordImg,
            alt: 'Change Password form',
            annotations: [
              { x: 39, y: 26, text: 'Current Password is required, even when you\'re changing your own password.' },
              { x: 34, y: 51, text: 'New password must be at least 6 characters.' },
              { x: 33, y: 56, text: 'This tab is visible to both Admins and Editors — it only ever affects your own login.' },
            ],
          },
        ],
        fields: [
          { label: 'Current Password', hint: '' },
          { label: 'New Password', hint: '' },
          { label: 'Confirm New Password', hint: '' },
        ],
        notes: ['New password must be at least 6 characters. This tab is visible to both Admins and Editors — it only affects your own login.'],
      },
      {
        title: 'Admin Users (Admin-only)',
        screenshots: [
          {
            src: settingsUsersListImg,
            alt: 'Admin Accounts list',
            label: 'List View',
            annotations: [
              { x: 50, y: 27, text: 'Add Account.' },
              { x: 35, y: 39, text: 'Role column — Admin or Editor; see the difference in the notes below.' },
              { x: 51, y: 39, text: 'Edit or delete this account (subject to the last-Admin restriction below).' },
            ],
          },
          {
            src: adminUserAddFormImg,
            alt: 'Add Admin Account form',
            label: 'Add Account',
            annotations: [
              { x: 49, y: 57, text: 'Role defaults to Editor here — switch to Admin if this account needs full access, including Site Settings.' },
              { x: 44, y: 71, text: 'Create Account — active immediately, no approval step.' },
              { x: 56, y: 71, text: 'Cancel without creating the account.' },
            ],
          },
          {
            src: adminUserEditFormImg,
            alt: 'Edit Admin Account form',
            label: 'Edit Account',
            annotations: [
              { x: 50, y: 52, text: 'Changing Role here takes effect immediately — but the very last remaining Admin can\'t be downgraded to Editor.' },
              { x: 50, y: 60, text: 'New Password — leave blank to keep their current password; doesn\'t require you to know it.' },
              { x: 44, y: 69, text: 'Save Changes.' },
            ],
          },
        ],
        fields: [
          { label: 'Email Address', hint: 'e.g. name@sankyomt.ph — doesn\'t need to be a real inbox, but an email keeps accounts easy to tell apart.' },
          { label: 'Password', hint: 'At least 6 characters. (Add Account only.)' },
          { label: 'Role', hint: 'Admin — full access, including Site Settings and other admin accounts. Editor — can manage Hero Slides, Categories, Products, Brands, Clients, Careers and Messages, but cannot open Site Settings or manage accounts.' },
          { label: 'New Password', hint: 'Editing an existing account only — leave blank to keep their current password. Doesn\'t require their current password.' },
        ],
        notes: [
          'To create an account: click "Add Account", fill in Email Address, Password, and Role, then "Create Account" — it\'s active immediately, no approval step.',
          'You can\'t delete your own account, and the very last remaining Admin account can\'t be deleted or downgraded to Editor — there must always be at least one Admin.',
          'Deleting an account asks: "Remove admin account \'[username]\'? This cannot be undone."',
        ],
      },
    ],
  },
];
